import { Button, Checkbox, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';

import { columns } from './grid-columns';
import { SiTrackPage } from '../../../components/si-track-page';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { contactApiKeyStore } from '../../../api/contact-api-key-store';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useState, useEffect } from 'react';
import { MoveContactDialog } from './move-contact/move-contact.dialog';
import { ViewMapContactDialog } from './view-mapping-contact.dialog';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Contact } from '../../../models/contact';
import { SaveHookFormDialog } from '../../../components/save-hook-form.dialog';
import { ContactForm } from './create-contact.form';
import { contactFormSchema } from './contact-form.schema';
import { SaveContactDialog } from './save-contact.dialog';
import { CustomGridCell } from './custom-grid-cell';
import { useSaveData } from '../../../hooks/useSaveData';
import ConfirmDialog from '../../../components/confirm-dialog';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
};

export const ContactsPage = () => {
  const isMoveContactEnabled = process.env.REACT_APP_MOVE_CONTACT_FLAG === 'true';

  const [contactId, setContactID] = useState<number>(0);
  const [customerID, setCustomerID] = useState<number>(0);
  const [emailReports, setEmailReports] = useState<any>(null);
  const [openDialog, setDialogAction] = useState<boolean>(false);
  const [openViewDialog, setViewDialogAction] = useState<boolean>(false);
  const [createItem, setCreateItem] = useState<any>();
  const [editItem, setEditItem] = useState<Partial<any>>();
  const [headerCheckboxes, setHeaderCheckboxes] = useState<Record<string, boolean>>({
    isSendReportEmail: false,
    sendReport: false,
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingField, setPendingField] = useState<string>('');
  const [pendingChecked, setPendingChecked] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  const {
    data,
    isLoading: isContactsLoading,
    isFetching,
  } = useLoadData<ApiResponse<any[]>>(contactApiKeyStore.getContacts());

  useEffect(() => {
    if (!data?.data?.length) return;
    setHeaderCheckboxes({
      isSendReportEmail: data.data.every((c: any) => !!c.isSendReportEmail),
      sendReport: data.data.every((c: any) => !!c.sendReport),
    });
  }, [data?.data]);

  const handleViewLeave = (contactData: any) => {
    setContactID(contactData?.contactsID ?? 0);
    setViewDialogAction(!openViewDialog);
  };

  const handleMoveContact = (contactDetails: any) => {
    setContactID(contactDetails?.contactsID ?? 0);
    setCustomerID(contactDetails?.customerID ?? 0);
    setEmailReports({
      ppiReportStatusText: contactDetails?.ppiReportStatusText,
      susReportStatusText: contactDetails?.susReportStatusText,
      otherReportStatusText: contactDetails?.otherReportStatusText,
      contactsNotificationText: contactDetails?.contactsNotificationText,
    });
    setDialogAction(true);
  };

  const handleEditContact = (contact: any) => {
    setEditItem({
      ...contact,
      // isMasterContacts: false,
      customer: { customerID: contact?.customerID ?? '', customerName: '' },
      contactRole: { contactsRoleID: contact?.contactsRoleID ?? '', contactsRoleName: '' },
    });
  };
  const handleCreateClose = () => {
    setCreateItem(undefined);
  };

  const handleEditClose = () => {
    setEditItem(undefined);
  };
  const handleCreateContact = () => {
    setCreateItem({ isActive: true, isMasterContacts: false });
  };

  const moveContactElement = (dataItem: any) => {
    return (
      <>
        {isMoveContactEnabled && (
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_MOVE_CONTACT]}>
            <IconButton aria-label="move" onClick={() => handleMoveContact(dataItem?.dataItem)}>
              <Tooltip title={<Typography variant="body1">Move</Typography>}>
                <ArrowCircleRightOutlinedIcon style={{ color: '#E74C3C' }} />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        )}
      </>
    );
  };

  const formatSubmitdata = (data: any) => {
    const contactsNotifications = data?.contactsNotifications?.map((item: any) => item.notificationValue).join(', ');
    return {
      ...data,
      customerID: data?.customer?.customerID,
      contactsRoleID: data?.contactRole?.contactsRoleID,
      contactsRoleName: data?.contactRole?.contactsRoleName,
      contactsNotificationIDs:
        data?.contactsNotificationIDs?.length > 0
          ? data?.contactsNotificationIDs?.map((ps: any) => ps.notificationReportStatusID)
          : [],
      ppiReportStatusIDs:
        data?.ppiReportStatusIDs?.length > 0
          ? data?.ppiReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID)
          : [],
      susReportStatusIDs:
        data?.susReportStatusIDs?.length > 0
          ? data?.susReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID)
          : [],
      otherReportStatusIDs:
        data?.otherReportStatusIDs?.length > 0
          ? data?.otherReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID)
          : [],
      contactMappingFrom: 'mastercontact',
    };
  };

  const { isLoading: isSendReportAllLoading, mutateAsync: mutateSendReportAll } = useSaveData<any>(
    contactApiKeyStore.enableSendAllReport(),
    () => contactApiKeyStore.getContacts()
  );

  const { isLoading: isFastFieldAllLoading, mutateAsync: mutateFastFieldAll } = useSaveData<any>(
    contactApiKeyStore.enableFastFieldAll(),
    () => contactApiKeyStore.getContacts()
  );

  const handleClose = () => {
    setIsConfirmOpen(false);
    setHeaderCheckboxes((prev) => ({ ...prev, [pendingField]: !pendingChecked }));
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    if (pendingField === 'isSendReportEmail') {
      // mutateSendReportAll({ route: `/Contact/UpdateContactSendReportAll?sendrporte=${pendingChecked}` });

      mutateSendReportAll({
        data: {
          sendrporte: pendingChecked,
        },
      });
    } else if (pendingField === 'sendReport') {
      mutateFastFieldAll({
        data: {
          fastFieldEmail: pendingChecked,
        },
      });
      // mutateFastFieldAll({ route: `/Contact/UpdateFastFieldEmailAll?fastFieldEmail=${pendingChecked}` });
    }
  };

  const handleHeaderCheckboxChange = (field: string, columnName: string, checked: boolean) => {
    setPendingField(field);
    setPendingChecked(checked);
    setHeaderCheckboxes((prev) => ({ ...prev, [field]: checked }));
    setConfirmMessage(`Are you sure you want to ${checked ? 'enable' : 'disable'} ${columnName} for all contacts?`);
    setIsConfirmOpen(true);
  };

  const renderCheckboxHeader = (field: string, columnName: string) => (props: any) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {props.title}
      <Checkbox
        checked={headerCheckboxes[field] ?? false}
        onChange={(e) => {
          e.stopPropagation();
          handleHeaderCheckboxChange(field, columnName, e.target.checked);
        }}
        size="small"
        sx={{ padding: '2px' }}
        onClick={(e) => e.stopPropagation()}
      />
    </span>
  );

  return (
    <SiTrackPage
      title="Contacts"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_CONTACT]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreateContact}
          >
            Add Contact
          </Button>
        </GuardedComponent>
      }
    >
      <Paper elevation={0} style={styling.paperStyle}>
        <SiTrackGrid
          fileTitle="master_contact"
          customCell={(field) =>
            CustomGridCell({
              field,
            })
          }
          customHeader={(field) => {
            if (field === 'sendReport') return renderCheckboxHeader(field, 'Fast Field');
            if (field === 'isSendReportEmail') return renderCheckboxHeader(field, 'Send Report');
          }}
          columns={columns}
          dataItemKey="contactsID"
          dataItems={data?.data}
          viewPopup={handleViewLeave}
          onEdit={handleEditContact}
          CustomComponent={moveContactElement}
          deleteMutationConfig={(deleteItem) => contactApiKeyStore.deleteContact(deleteItem.contactsID)}
          actionPermissions={{
            edit: UserPermissionName.EDIT_CONTACT,
            delete: UserPermissionName.DELETE_CONTACT,
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
          isBusy={isContactsLoading || isFetching || isSendReportAllLoading || isFastFieldAllLoading}
        />

        {createItem && (
          <SaveHookFormDialog
            title={createItem.contactsID ? 'UPDATE CONTACT' : 'CREATE CONTACT'}
            config={{
              saveConfig: createItem.contactsID
                ? contactApiKeyStore.updateContact()
                : contactApiKeyStore.createContact(),
              invalidateRoute: () => contactApiKeyStore.getContacts(),
            }}
            data={createItem}
            yupSchema={contactFormSchema}
            formatSubmitdata={formatSubmitdata}
            onClose={handleCreateClose}
            size="xl"
          >
            <ContactForm />
          </SaveHookFormDialog>
        )}

        {editItem && <SaveContactDialog data={editItem} onClose={handleEditClose} />}
        {openDialog && (
          <MoveContactDialog
            contactData={data?.data}
            contactID={contactId}
            customerID={customerID}
            emailReports={emailReports}
            onClosed={() => setDialogAction(false)}
          />
        )}
        {openViewDialog && <ViewMapContactDialog contactID={contactId} onClosed={() => setViewDialogAction(false)} />}

        <ConfirmDialog
          open={isConfirmOpen}
          title="CONFIRM UPDATE"
          message={confirmMessage}
          onClose={handleClose}
          onConfirm={handleConfirm}
          isLoading={isSendReportAllLoading || isFastFieldAllLoading}
        />
      </Paper>
    </SiTrackPage>
  );
};
